import { useEffect, useState } from 'react';
import type { ProductCarouselItem } from '@noths/polaris-design-system-candidate-components';
import type { CurrencyCode } from '@noths/polaris-dev-ts-types';

import type { PlpPreviewResponse } from 'src/services/plpPreview/types';
import type { TrackingProductData } from 'src/tracking/common/products';
import { logger } from 'src/utils/serverLogger';
import { transformPlpPreviewProductToProductCarouselItem } from '../transformer';
import { transformPlpPreviewProductToTrackingProductData } from '../utils/transformPlpPreviewProductToTrackingProductData';

export const PLP_PREVIEW_PRODUCT_LIMIT = 10;
export const PLP_PREVIEW_FETCH_TIMEOUT_MS = 5000;

export interface UsePlpPreviewProps {
  currency: CurrencyCode;
  plpUrl: string;
}

export const usePlpPreview = ({ currency, plpUrl }: UsePlpPreviewProps) => {
  const [products, setProducts] = useState<ProductCarouselItem[] | null>(null);
  const [productsToTrack, setProductsToTrack] = useState<TrackingProductData[]>([]);

  useEffect(() => {
    const fetchPreview = async () => {
      try {
        const params = new URLSearchParams({
          currency,
          limit: String(PLP_PREVIEW_PRODUCT_LIMIT),
          url: plpUrl,
        });
        const response = await fetch(`/productlistings-api/plp-preview?${params}`, {
          signal: AbortSignal.timeout(PLP_PREVIEW_FETCH_TIMEOUT_MS),
        });

        if (!response.ok) {
          throw new Error(`plp-preview responded with status ${response.status}`);
        }

        const data: PlpPreviewResponse = await response.json();

        /* Taken once for the whole set so that every card's estimated delivery is
           dated from the same instant, rather than each from whenever its own
           mapping happened to run. */
        const now = new Date();

        setProducts(
          data.products.map((product) =>
            transformPlpPreviewProductToProductCarouselItem(product, { currency, now }),
          ),
        );
        setProductsToTrack(data.products.map(transformPlpPreviewProductToTrackingProductData));
      } catch (error) {
        // Collapse the block without a trace on any failure (endpoint error, timeout, bad JSON).
        setProducts([]);
        logger.error('Error fetching PLP preview:', error);
      }
    };

    fetchPreview();
  }, [currency, plpUrl]);

  return { products, productsToTrack };
};
